import { OrdersState } from 'app/store/orders/OrdersState';
import {
    isOrderMessageDataItemHideOrder,
    isOrderMessageDataItemRemoveMultipleOrders,
    isOrderMessageDataItemReplaceOrder,
    OrderDataMessageDataItem,
} from 'communication/types/order/GetOrderColumnsMessage';
import Order from 'communication/types/order/Order';
import {
    OrderStreamUpdatedEvent,
    OrderStreamUpdatedEventBuilder,
} from 'app/store/orders/orderStreamsUpdatedEvent';
import omit from 'lodash/omit';
import { selectOrdersState } from 'app/store/orders/selectors';
import { ordersStreamUpdated, setOrdersAction } from 'app/store/orders/actions';
import { ActionsDeferredDispatcher } from 'store/ActionsDispatcher';
import { using as usingStatement } from 'using-statement';
import { IndigoStore } from 'store/store';
import isNil from 'lodash/isNil';
import { safeCalculatePercent } from 'utils/percent';

/*
 * Applies a batch of orders to the existing orders state, and dispatches actions to set the orders and raise an orders updated event
 */
export function processBatchedOrders(
    store: IndigoStore,
    batch: OrderDataMessageDataItem[],
    refreshDone: boolean,
    clearOrders: boolean
) {
    const prevOrdersState = selectOrdersState(store.getState());
    const { nextOrders, updatedEvent } = reduceBatchedOrders(
        prevOrdersState,
        batch,
        clearOrders
    );

    updatedEvent.completesRefresh = refreshDone;
    updatedEvent.cleared = clearOrders;

    const batchUpdateInProgress = updatedEvent.completesRefresh
        ? false
        : prevOrdersState.batchUpdateInProgress;

    updatedEvent.batchUpdateInProgress = batchUpdateInProgress;

    if (
        refreshDone ||
        clearOrders ||
        updatedEvent.updated.length > 0 ||
        updatedEvent.added.length > 0 ||
        updatedEvent.removed.length > 0 ||
        updatedEvent.multipleOrdersRemoved > 0
    ) {
        usingStatement(new ActionsDeferredDispatcher(store), (dispatcher) => {
            dispatcher.dispatch(
                setOrdersAction(nextOrders, batchUpdateInProgress)
            );
            dispatcher.dispatch(ordersStreamUpdated(updatedEvent));
        });
    }
}

function reduceBatchedOrders(
    state: OrdersState,
    data: OrderDataMessageDataItem[],
    clearOrders: boolean
): { nextOrders: Order[]; updatedEvent: OrderStreamUpdatedEvent } {
    const nextPartialOrders = data;
    const updatedEventBuilder = new OrderStreamUpdatedEventBuilder();

    const ordersToMerge = nextPartialOrders || [];

    const nextOrders = clearOrders || !state.data ? [] : [...state.data];

    let dataItem: OrderDataMessageDataItem;
    let idx: number;
    for (let i = 0; i < ordersToMerge.length; i++) {
        dataItem = ordersToMerge[i];

        if (isOrderMessageDataItemRemoveMultipleOrders(dataItem)) {
            for (
                let multRemIdx = dataItem.clearOff;
                multRemIdx <= nextOrders.length;
                multRemIdx = multRemIdx + dataItem.clearMod
            ) {
                const existing = nextOrders[multRemIdx];
                if (existing !== undefined) {
                    updatedEventBuilder.event.multipleOrdersRemoved++;
                    delete nextOrders[multRemIdx];
                }
            }
        } else {
            idx = dataItem.idx;

            if (isOrderMessageDataItemHideOrder(dataItem)) {
                const existing = nextOrders[idx];
                if (existing) {
                    delete nextOrders[idx];
                    updatedEventBuilder.removed(existing);
                }
            } else {
                const existing = nextOrders[idx];

                if (isOrderMessageDataItemReplaceOrder(dataItem)) {
                    // Complete replacement...
                    nextOrders[idx] = { ...omit(dataItem, 'showOrder') };
                } else {
                    // Patch...
                    nextOrders[idx] = { ...nextOrders[idx], ...dataItem };
                }

                if (existing) {
                    updatedEventBuilder.updated(existing, nextOrders[idx]);
                } else {
                    updatedEventBuilder.added(nextOrders[idx]);
                }

                calculateClientDerivedColumns(nextOrders[idx]);
            }
        }
    }
    return {
        nextOrders: nextOrders as Order[],
        updatedEvent: updatedEventBuilder.event,
    };
}

export function calculateClientDerivedColumns(order: Order) {
    if (!isNil(order.pnc) && !isNil(order.lp)) {
        const diff = order.lp - order.pnc;
        order.movement = safeCalculatePercent(diff, order.pnc);
    }
}
